import axios from 'axios';

const ANALYTICS_API_URL = import.meta.env.VITE_ANALYTICS_API_URL || 'http://localhost:3002/api/v1';

// Create axios instance for analytics backend
const analyticsClient = axios.create({
    baseURL: ANALYTICS_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add admin auth token
analyticsClient.interceptors.request.use(
    async (config) => {
        // Get Clerk token from admin Clerk instance
        const token = await window.Clerk?.session?.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
analyticsClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        const message = error.response?.data?.message || error.message || 'Analytics API error';
        return Promise.reject(new Error(message));
    }
);

// ==================== ANALYTICS ENDPOINTS ====================

/**
 * Get overview metrics for dashboard
 * @param {Object} params - { period: '7d'|'30d'|'90d', startDate?: string, endDate?: string }
 */
export const getOverview = (params) =>
    analyticsClient.get('/admin/overview', { params });

/**
 * Get retention cohort data
 * @param {Object} params - { days?: number }
 */
export const getRetention = (params) =>
    analyticsClient.get('/admin/retention', { params });

/**
 * Get top screens by view count
 * @param {Object} params - { period: '7d'|'30d'|'90d', limit?: number }
 */
export const getScreenStats = (params) =>
    analyticsClient.get('/admin/screens', { params });

/**
 * Get feature usage statistics
 * @param {Object} params - { period: '7d'|'30d'|'90d' }
 */
export const getFeatureStats = (params) =>
    analyticsClient.get('/admin/features', { params });

/**
 * Get user activity for a specific user
 * @param {string} userId - The user ID
 * @param {Object} params - { limit?: number }
 */
export const getUserActivity = (userId, params) =>
    analyticsClient.get(`/admin/users/${userId}/activity`, { params });

/**
 * Get real-time stats
 */
export const getRealTimeStats = () =>
    analyticsClient.get('/admin/realtime');

/**
 * Get snapshot metrics (on-demand calculation from raw events)
 * Use this to see current metrics before daily aggregation runs
 * @param {Object} params - { periodDays?: number } (default 7)
 */
export const getSnapshotMetrics = (params) =>
    analyticsClient.get('/admin/snapshot', { params });

/**
 * Get analytics summary (for reset confirmation)
 */
export const getAnalyticsSummary = () =>
    analyticsClient.get('/admin/summary');

/**
 * Reset all analytics data
 * WARNING: This is destructive and irreversible
 * @param {string} confirmation - Must be exactly "i confirm"
 */
export const resetAnalytics = (confirmation) =>
    analyticsClient.post('/admin/reset', { confirmation });

export default analyticsClient;

